import React, { useState, useMemo, useCallback, useEffect } from "react";
import ServiceCard from "../components/ServiceCard";
import { SERVICES } from "../data";
import "../styles/Services.css";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function normalizeService(raw, index) {
  if (!raw) {
    return null;
  }

  if (typeof raw === "string") {
    const base = raw
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    return {
      title: raw,
      slug: base,
      category: "general",
      image: "",
    };
  }

  const title =
    typeof raw.title === "string" && raw.title.trim().length > 0
      ? raw.title.trim()
      : null;

  const slug =
    typeof raw.slug === "string" && raw.slug.trim().length > 0
      ? raw.slug.trim()
      : null;

  const image = typeof raw.image === "string" ? raw.image : "";

  if (!title || !slug) {
    return null;
  }

  const lower = slug.toLowerCase();
  let category = "other";

  if (lower.includes("ceramic") || lower.includes("ppf") || lower.includes("wrap")) {
    category = "protection";
  } else if (
    lower.includes("detailing") ||
    lower.includes("spa") ||
    lower.includes("care")
  ) {
    category = "detailing";
  } else if (lower.includes("painting")) {
    category = "body-shop";
  } else if (lower.includes("obd") || lower.includes("ac")) {
    category = "diagnostics";
  }

  return {
    title,
    slug,
    category,
    image,
  };
}

function useNormalizedServices() {
  const services = useMemo(() => {
    if (!Array.isArray(SERVICES)) {
      return [];
    }
    return SERVICES.map((s, idx) => normalizeService(s, idx)).filter(Boolean);
  }, []);
  return services;
}

function useScrollToTopOnMount() {
  useEffect(() => {
    try {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch {
      if (typeof window !== "undefined") {
        window.scrollTo(0, 0);
      }
    }
  }, []);
}

function useServiceBooking(user, navigate, setSelectedSlug) {
  const handleBook = useCallback(
    (service) => {
      if (!service || !service.slug || !service.title) {
        toast.error("Invalid service");
        return;
      }

      const { title, slug } = service;

      try {
        localStorage.setItem("prefService", title);
        localStorage.setItem("prefServiceSlug", slug);
      } catch {
      }

      setSelectedSlug(slug);
      toast.info(`Selected ${title}`, { position: "top-center" });

      if (!user) {
        toast.warn("Login required to continue", { position: "top-center" });
        const next = `/book?service=${encodeURIComponent(slug)}`;
        navigate(`/login?redirect=${encodeURIComponent(next)}`);
        return;
      }

      toast.success(`Booking started for ${title}`, { position: "top-center" });
      navigate(`/book?service=${encodeURIComponent(slug)}`);
    },
    [user, navigate, setSelectedSlug]
  );

  return handleBook;
}

function ServicesHeader() {
  return (
    <motion.div
      className="services-header"
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      <h2 className="services-title gradient-text">Our Services</h2>
      <p className="services-subtitle">
        Explore professional car care solutions. Choose a service and begin your booking instantly.
      </p>
    </motion.div>
  );
}

function EmptyServicesState() {
  return (
    <div className="services-empty">
      <p className="muted">
        No services currently available. Please check back soon.
      </p>
    </div>
  );
}

function ServiceSkeletonCard({ index }) {
  return (
    <motion.div
      className="service-skeleton-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <div className="sk-img" />
      <div className="sk-line sk-title" />
      <div className="sk-line sk-text" />
      <div className="sk-btn" />
    </motion.div>
  );
}

function SkeletonGrid() {
  return (
    <div className="services-list">
      {Array.from({ length: 6 }).map((_, idx) => (
        <ServiceSkeletonCard key={idx} index={idx} />
      ))}
    </div>
  );
}

function FilterSidebar({ filters, activeFilter, onChange }) {
  return (
    <aside className="services-filters">
      <h4 className="filters-title">Filter</h4>

      <button
        type="button"
        className={
          activeFilter === "all"
            ? "filter-chip filter-chip-active"
            : "filter-chip"
        }
        onClick={() => onChange("all")}
      >
        All services
      </button>

      {filters.map((f) => (
        <button
          key={f.value}
          type="button"
          className={
            activeFilter === f.value
              ? "filter-chip filter-chip-active"
              : "filter-chip"
          }
          onClick={() => onChange(f.value)}
        >
          {f.label}
        </button>
      ))}
    </aside>
  );
}

function ServicesStats({ total, byCategory }) {
  const display = typeof total === "number" ? total : 0;

  return (
    <div className="services-stats-bar">
      <div className="services-stat-item">
        <span className="services-stat-label">Total services</span>
        <span className="services-stat-value">{display}</span>
      </div>
      <div className="services-stat-split">
        {byCategory.map((item) => (
          <div key={item.value} className="services-stat-pill">
            <span className="services-stat-pill-label">{item.label}</span>
            <span className="services-stat-pill-value">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ServicesGrid({ services, selectedSlug, onBook, onCardClick }) {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.25,
      },
    },
  };

  if (!services.length) {
    return <EmptyServicesState />;
  }

  return (
    <motion.div
      className="services-list"
      variants={container}
      initial="hidden"
      animate="show"
    >
      {services.map((service) => {
        const active = selectedSlug === service.slug;

        return (
          <div
            key={service.slug}
            className="service-card-shell"
            onClick={() => onCardClick(service)}
          >
            <ServiceCard
              title={service.title}
              description={`Premium ${service.title.toLowerCase()} by expert technicians delivering superior care.`}
              image={service.image}
              isSelected={active}
              highlightLabel={active ? "Selected" : ""}
              disabled={false}
              onBook={() => onBook(service)}
            />
          </div>
        );
      })}
    </motion.div>
  );
}

function ServiceModal({ service, open, onClose, onBook }) {
  return (
    <AnimatePresence>
      {open && service && (
        <motion.div
          className="service-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="service-modal"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="service-modal-header">
              <h3>{service.title}</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="service-modal-body">
              <p>
                Performed by trained technicians using premium grade materials
                designed for long lasting results.
              </p>
              <ul className="modal-points">
                <li>Expert execution</li>
                <li>High-end tools and products</li>
                <li>Professional care for every vehicle</li>
              </ul>
            </div>
            <div className="service-modal-footer">
              <button
                type="button"
                className="btn modal-secondary"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="btn modal-primary"
                onClick={() => onBook(service)}
              >
                Book this service
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function useServiceStats(services) {
  const stats = useMemo(() => {
    if (!services.length) {
      return {
        total: 0,
        byCategory: [],
      };
    }

    const buckets = new Map();

    services.forEach((s) => {
      const key = s.category || "other";
      const count = buckets.get(key) || 0;
      buckets.set(key, count + 1);
    });

    const byCategory = Array.from(buckets.entries()).map(([value, count]) => {
      if (value === "detailing") {
        return {
          value,
          label: "Detailing & Spa",
          count,
        };
      }
      if (value === "protection") {
        return {
          value,
          label: "Paint Protection",
          count,
        };
      }
      if (value === "diagnostics") {
        return {
          value,
          label: "Diagnostics",
          count,
        };
      }
      if (value === "body-shop") {
        return {
          value,
          label: "Body & Painting",
          count,
        };
      }
      return {
        value,
        label: "Other",
        count,
      };
    });

    return {
      total: services.length,
      byCategory,
    };
  }, [services]);

  return stats;
}

function ServicesToolbar({ activeFilter, onReset }) {
  return (
    <div className="services-toolbar">
      <div className="services-toolbar-left">
        <span className="services-toolbar-title">Browse all services</span>
        <span className="services-toolbar-sub">
          Filter or tap Book to begin a booking instantly.
        </span>
      </div>
      <div className="services-toolbar-right">
        <button
          type="button"
          className="toolbar-reset-btn"
          onClick={() => onReset("all")}
          disabled={activeFilter === "all"}
        >
          Clear filters
        </button>
      </div>
    </div>
  );
}

export default function Services() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const services = useNormalizedServices();

  useScrollToTopOnMount();

  const [selectedSlug, setSelectedSlug] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [modalService, setModalService] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => setIsLoading(false), 380);
    return () => clearTimeout(id);
  }, []);

  const handleBook = useServiceBooking(user, navigate, setSelectedSlug);

  const filters = useMemo(() => {
    const store = new Set(services.map((s) => s.category));
    return Array.from(store).map((cat) => {
      if (cat === "detailing") {
        return { value: cat, label: "Detailing & Spa" };
      }
      if (cat === "protection") {
        return { value: cat, label: "Paint Protection" };
      }
      if (cat === "diagnostics") {
        return { value: cat, label: "Diagnostics" };
      }
      if (cat === "body-shop") {
        return { value: cat, label: "Body & Painting" };
      }
      return { value: cat, label: "Other" };
    });
  }, [services]);

  const visibleServices = useMemo(() => {
    if (activeFilter === "all") {
      return services;
    }
    return services.filter((s) => s.category === activeFilter);
  }, [services, activeFilter]);

  const stats = useServiceStats(services);

  const handleFilterChange = useCallback((value) => {
    setActiveFilter(value);
    setSelectedSlug(null);
  }, []);

  const handleCardClick = useCallback((service) => {
    if (!service) {
      return;
    }
    setModalService(service);
    setSelectedSlug(service.slug);
    toast.info(`Viewing details for ${service.title}`, {
      position: "top-center",
    });
  }, []);

  return (
    <section className="container page services-page">
      <ServicesHeader />
      <ServicesStats total={stats.total} byCategory={stats.byCategory} />
      <div className="services-layout">
        <FilterSidebar
          filters={filters}
          activeFilter={activeFilter}
          onChange={handleFilterChange}
        />
        <div className="services-main">
          <ServicesToolbar
            activeFilter={activeFilter}
            onReset={handleFilterChange}
          />
          {isLoading ? (
            <SkeletonGrid />
          ) : (
            <div className="services-grid-wrapper">
              <ServicesGrid
                services={visibleServices}
                selectedSlug={selectedSlug}
                onBook={handleBook}
                onCardClick={handleCardClick}
              />
            </div>
          )}
        </div>
      </div>
      <ServiceModal
        service={modalService}
        open={Boolean(modalService)}
        onClose={() => setModalService(null)}
        onBook={handleBook}
      />
    </section>
  );
}
